import { useContext, useEffect, useCallback, useMemo } from "react";
import { Card, Dialog, Link, Box } from "@mui/material";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getobj, postobj } from "../apicall/apicall";
import { useState } from "react";
import {
  boxStyle,
  StyledTableCell,
  StyledTableRow,
  searchStyle,
  headerStyle,
} from "../style/style";
import SearchSharpIcon from "@mui/icons-material/SearchSharp";
import Input from "@mui/material/Input";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Pagination from "@mui/material/Pagination";
import "react-notifications/lib/notifications.css";
import {
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DialogActions, DialogTitle, DialogContent } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import LoadingButton from "@mui/lab/LoadingButton";

import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import MonthlyBillingStat from "./MonthlyBillingStat";

export default function CustomerBilling() {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [refresh, setRefresh] = useState(false);
  const [search, setSearch] = useState("");
  const [loaded, setLoaded] = useState(true);
  const [alignment, setAlignment] = useState("month");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [statOpen, setStatOpen] = useState(false);
  const [statData, setStatData] = useState(null);
  const [isExporting, setIsExporting] = useState({});

  const currentYear = new Date().getFullYear();
  const years = useMemo(
    () => Array.from({ length: 10 }, (_, i) => currentYear - i),
    [currentYear],
  );

  const months = useMemo(
    () => [
      { value: 1, label: "Jan" },
      { value: 2, label: "Feb" },
      { value: 3, label: "Mar" },
      { value: 4, label: "Apr" },
      { value: 5, label: "May" },
      { value: 6, label: "Jun" },
      { value: 7, label: "Jul" },
      { value: 8, label: "Aug" },
      { value: 9, label: "Sep" },
      { value: 10, label: "Oct" },
      { value: 11, label: "Nov" },
      { value: 12, label: "Dec" },
    ],
    [],
  );

  // Debounced search handler
  const handleSearch = useCallback(
    (e) => {
      setPage(1);
      e?.preventDefault();
      setRefresh(!refresh);
    },
    [refresh],
  );

  // Fetch bills data
  const fetchBills = useCallback(
    (pageNum, searchQuery = "") => {
      setLoaded(true);
      let url = "";

      // Build start_date parameter
      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-01`;

      if (alignment === "month") {
        url = `${process.env.REACT_APP_ZEWA_URL}user/billing-details/0/Monthly/?page=${pageNum}&start_date=${startDate}`;
      } else {
        url = `${process.env.REACT_APP_ZEWA_URL}user/billing-details/0/Yearly/?page=${pageNum}&start_date=${startDate}`;
      }

      if (searchQuery) {
        url += `&search=${searchQuery}`;
      }
      console.log("Get URL:", url);

      getobj(url)
        .then((res) => {
          console.log("Fetched bills:", res.results);
          if (res && res.count !== undefined && res.results) {
            console.log("Total count:", res.results.length);
            setTotalPage(Math.ceil(res.count / 13)); // Changed from 12 to 13 (paginator.page_size)
            setBills(res.results);
          } else {
            setBills([]);
            setTotalPage(0);
          }
          setLoaded(false);
        })
        .catch((err) => {
          console.error("Error fetching bills:", err);
          setBills([]);
          setTotalPage(0);
          setLoaded(false);
        });
    },
    [alignment, selectedYear, selectedMonth],
  );

  // Fetch bills on component mount and when filters change
  useEffect(() => {
    setPage(1);
    fetchBills(1, search);
  }, [alignment, selectedYear, selectedMonth, refresh]);

  // Handle page change
  const handlePageChange = useCallback(
    (event, value) => {
      setPage(value);
      fetchBills(value, search);
    },
    [fetchBills, search],
  );

  // Handle alignment toggle
  const handleAlignmentChange = useCallback((event, newAlignment) => {
    if (newAlignment) {
      setAlignment(newAlignment);
      setPage(1);
    }
  }, []);

  // Handle month/year selection
  const handleYearChange = useCallback((event) => {
    const year = parseInt(event.target.value);
    setSelectedYear(year);
    setPage(1);
  }, []);

  const handleMonthChange = useCallback((event) => {
    const month = parseInt(event.target.value);
    setSelectedMonth(month);
    setPage(1);
  }, []);

  // Handle device stat dialog
  const handleStatDialog = useCallback((customerId, invoice, deviceType) => {
    setStatData({
      customerId,
      invoice,
      deviceType,
    });
    setStatOpen(true);
  }, []);

  // Handle CSV download
  const handleDownload = useCallback(() => {
    // If no bills are available, show a warning and return early
    if (bills.length === 0) {
      NotificationManager.warning("No data to export", "Warning", 2000);
      return;
    }

    setIsExporting((prev) => ({ ...prev, export: true }));

    // Build start_date parameter
    const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-01`;
    let url = `${process.env.REACT_APP_ZEWA_URL}user/billing-details/0/Monthly/?start_date=${startDate}&export=true`;
    if (search) {
      url += `&search=${search}`;
    }

    // console.log("Export URL:", url);
    getobj(url)
      .then((response) => {
        const csvContent = response.map((row) => row.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        const urlBlob = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = urlBlob;
        link.download = `customer_billing_${selectedYear}_${selectedMonth}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        NotificationManager.success(
          "Customer billing report exported successfully",
          "Success",
          2000,
        );
      })
      .catch((err) => {
        console.error("Error exporting CSV:", err);
        NotificationManager.error("Error exporting CSV", "Error", 2000);
      })
      .finally(() => {
        setIsExporting((prev) => ({ ...prev, export: false }));
      });
  }, [bills, selectedYear, selectedMonth]);

  // Handle detail page navigation
  const handleDetailAction = useCallback(
    (customerId, invoice, customerName) => {
      // Store billing parameters in sessionStorage
      sessionStorage.setItem("billingCustomerId", customerId);
      sessionStorage.setItem("billingCustomerName", customerName);
      sessionStorage.setItem("billingMonth", selectedMonth);
      sessionStorage.setItem("billingYear", selectedYear);
      // Store the actual invoice dates from the bill object
      if (invoice && invoice.from_utc) {
        sessionStorage.setItem("billingFromUtc", invoice.from_utc);
      }
      if (invoice && invoice.to_utc) {
        sessionStorage.setItem("billingToUtc", invoice.to_utc);
      }
      navigate("/admin");
    },
    [navigate, selectedMonth, selectedYear],
  );

  // Memoize the bills list to prevent re-renders
  const memoizedBills = useMemo(() => bills, [bills]);

  return (
    <div>
      <Card sx={boxStyle}>
        <Card sx={headerStyle}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <span>Customer Billing</span>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              {/* Month/Year Selector */}
              {/* <ToggleButtonGroup
                value={alignment}
                exclusive
                onChange={handleAlignmentChange}
                size="small"
              >
                <ToggleButton value="month">Month</ToggleButton>
                <ToggleButton value="year">Year</ToggleButton>
              </ToggleButtonGroup> */}

              {/* Year Dropdown */}
              <TextField
                select
                value={selectedYear}
                onChange={handleYearChange}
                size="small"
                sx={{ width: "100px" }}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>

              {/* Month Dropdown */}
              {alignment === "month" && (
                <TextField
                  select
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  size="small"
                  sx={{ width: "100px" }}
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>
                      {month.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {/* Export Button */}
              <LoadingButton
                startIcon={<GetAppIcon />}
                loading={Object.values(isExporting).some((v) => v)}
                onClick={handleDownload}
                variant="outlined"
              >
                Export
              </LoadingButton>

              {/* Search Bar */}
              <form style={searchStyle} onSubmit={handleSearch}>
                <FormControl variant="standard" sx={searchStyle}>
                  <InputLabel htmlFor="billing-search">
                    Search Organization ...
                  </InputLabel>
                  <Input
                    id="billing-search"
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchSharpIcon color="primary" />
                      </InputAdornment>
                    }
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </FormControl>
              </form>
            </Box>
          </Box>
        </Card>
      </Card>

      <Card sx={boxStyle}>
        {loaded ? (
          <img
            src="https://zewa-logo-bucket.s3.amazonaws.com/logo/Spinner-1s-200px.gif"
            alt="loading..."
          />
        ) : memoizedBills && memoizedBills.length > 0 ? (
          <TableContainer>
            <Table aria-label="billing table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>
                    <b>Organization Name</b>
                  </StyledTableCell>
                  <StyledTableCell>
                    <b>Distributor Name</b>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <b>Cellular Devices</b>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <b>Bluetooth Devices</b>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <b>Hubs</b>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <b>Actions</b>
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {memoizedBills.map((bill) => (
                  <StyledTableRow key={bill.id}>
                    <StyledTableCell>
                      {bill.customer_name || "NA"}
                    </StyledTableCell>
                    <StyledTableCell>
                      {bill.distributor_name || "NA"}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Link
                        onClick={() =>
                          handleStatDialog(bill.customer_id, bill, "cellular")
                        }
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                      >
                        {bill.cellular_device || 0}
                      </Link>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Link
                        onClick={() =>
                          handleStatDialog(bill.customer_id, bill, "bluetooth")
                        }
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                      >
                        {bill.bluetooth_device || 0}
                      </Link>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Link
                        onClick={() =>
                          handleStatDialog(bill.customer_id, bill, "hub")
                        }
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                      >
                        {bill.hubs || 0}
                      </Link>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Button
                        onClick={() =>
                          handleDetailAction(
                            bill.customer_id,
                            bill,
                            bill.customer_name,
                          )
                        }
                        variant="outlined"
                        size="small"
                      >
                        View Details
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              count={totalPage}
              page={page}
              variant="outlined"
              color="primary"
              onChange={handlePageChange}
              sx={{ display: "flex", justifyContent: "center", padding: 2 }}
            />
          </TableContainer>
        ) : (
          <div style={{ padding: "20px", textAlign: "center" }}>
            No customer billing data available for the selected period.
          </div>
        )}
        <NotificationContainer />

        {/* Monthly Billing Stat Dialog */}
        <Dialog
          open={statOpen}
          maxWidth="xl"
          onClose={() => setStatOpen(false)}
        >
          <DialogContent style={{ overflow: "hidden" }}>
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                Device Statistics
                <CloseIcon
                  sx={{ cursor: "pointer" }}
                  onClick={() => setStatOpen(false)}
                />
              </Box>
            </DialogTitle>
            {statData && (
              <MonthlyBillingStat
                customerId={statData.customerId}
                data={{
                  date: {
                    from_utc: statData.invoice.from_utc,
                  },
                  data: statData.deviceType,
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
}
